import { Colors, Text } from '@amelio/ui-library';

export interface YesNoResultProps {
    /** Percentage of Yes responses (0–100) */
    yesPercentage: number;
    /** Percentage of No responses (0–100) */
    noPercentage: number;
    /** Optional class override */
    className?: string;
    /** Root test id */
    dataTestId?: string;
}

const BAR_HEIGHT = 16;

const clampPercentage = (value: number) => Math.max(0, Math.min(100, value));

const YesNoResult = ({
    yesPercentage,
    noPercentage,
    className = '',
    dataTestId = 'yes-no-result',
}: YesNoResultProps) => {
    const getBarGradient = (value: number, type: 'yes' | 'no'): string => {
        const percentage = clampPercentage(value);

        if (type === 'yes') {
            if (percentage <= 10) {
                return Colors.PRIMARY_700;
            }

            if (percentage <= 30) {
                return `linear-gradient(to right, ${Colors.PRIMARY_700} 0%, ${Colors.PRIMARY} 100%)`;
            }

            return `linear-gradient(
            to right,
            ${Colors.PRIMARY_700} 0%,
            ${Colors.PRIMARY} 50%,
            ${Colors.PRIMARY_400} 100%
        )`;
        }

        // NO bar
        if (percentage <= 10) {
            return Colors.SECONDARY_900;
        }

        if (percentage <= 30) {
            return `linear-gradient(to right, ${Colors.SECONDARY_900} 0%, ${Colors.SECONDARY_700} 100%)`;
        }

        return `linear-gradient(
        to right,
        ${Colors.SECONDARY_900} 0%,
        ${Colors.SECONDARY_700} 50%,
        ${Colors.SECONDARY_400} 100%
    )`;
    };

    const rows = [
        {
            label: 'YES',
            value: clampPercentage(yesPercentage),
            valueColor: Colors.PRIMARY,
            type: 'yes' as const,
            testId: 'yes',
        },
        {
            label: 'NO',
            value: clampPercentage(noPercentage),
            valueColor: Colors.SECONDARY_800,
            type: 'no' as const,
            testId: 'no',
        },
    ];

    return (
        <div
            className={`w-full space-y-8 pt-6 pb-8 ${className}`}
            data-testid={dataTestId}
        >
            {rows.map((row) => (
                <div
                    key={row.label}
                    className={'space-y-2'}
                    data-testid={`${dataTestId}-${row.testId}`}
                >
                    {/* Label + Percentage */}
                    <div className={'flex items-center justify-between'}>
                        <Text
                            size={'small'}
                            color={Colors.BASE_500}
                            className={'uppercase'}
                        >
                            {row.label}
                        </Text>

                        <Text
                            size={'large'}
                            weight={'bold'}
                            color={row.valueColor}
                        >
                            {row.value}%
                        </Text>
                    </div>

                    {/* Progress Bar */}
                    <div
                        className={'bg-base-100 relative w-full overflow-hidden rounded-full'}
                        style={{ height: BAR_HEIGHT }}
                        data-testid={`${dataTestId}-${row.testId}-bar`}
                    >
                        <div
                            className={'h-full rounded-full transition-all'}
                            style={{
                                width: `${row.value}%`,
                                background: getBarGradient(row.value, row.type),
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default YesNoResult;
export { YesNoResult };
